import React, { useMemo } from 'react';
import { Row, Col, Card, Typography } from 'antd';
import {
  HomeOutlined,
  LineChartOutlined,
  ShoppingOutlined,
  ToolOutlined,
  AccountBookOutlined,
  SafetyCertificateOutlined,
  ApartmentOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';

const { Text, Title } = Typography;

const DOMAIN_CARDS: { path: string; title: string; description: string; icon: React.ReactNode }[] = [
  {
    path: '/unified',
    title: 'Unified workboard',
    description: 'Finance, SCM, and sales exceptions in one place.',
    icon: <ApartmentOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
  {
    path: '/home/tasks',
    title: 'My tasks',
    description: 'Personal worklist and follow-ups.',
    icon: <HomeOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
  {
    path: '/growth/dashboard',
    title: 'Growth & Revenue',
    description: 'Pipeline, orders, and billing health.',
    icon: <LineChartOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
  {
    path: '/scm/dashboard',
    title: 'Supply chain',
    description: 'Procurement, inventory, and logistics.',
    icon: <ShoppingOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
  {
    path: '/mfg/control-tower',
    title: 'Manufacturing',
    description: 'Production, work orders, and capacity.',
    icon: <ToolOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
  {
    path: '/fin/dashboard',
    title: 'Finance',
    description: 'GL, cash, and period close.',
    icon: <AccountBookOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
  {
    path: '/qms/dashboard',
    title: 'Quality',
    description: 'Inspections, NCRs, and compliance.',
    icon: <SafetyCertificateOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
  {
    path: '/hr/dashboard',
    title: 'HRMS',
    description: 'Workforce, leave, compliance, and talent.',
    icon: <TeamOutlined style={{ fontSize: 28, color: '#145A54' }} />,
  },
];

export const ExecutiveDomainHub: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canAccess = (path: string) =>
    canAccessRouteByPermissions(path, user?.permissions ?? [], !!user?.is_super_admin);

  const visible = useMemo(() => DOMAIN_CARDS.filter((c) => canAccess(c.path)), [user?.permissions, user?.is_super_admin]);

  if (visible.length === 0) return null;

  return (
    <div style={{ marginBottom: EnterpriseTokens.spacing.xl }}>
      <Title level={4} style={{ ...EnterpriseTokens.typography.sectionTitle, marginBottom: EnterpriseTokens.spacing.md }}>
        Domain entry
      </Title>
      <Row gutter={[16, 16]}>
        {visible.map((d) => (
          <Col xs={24} sm={12} md={8} lg={6} key={d.path}>
            <Card
              hoverable
              onClick={() => navigate(d.path)}
              style={{ border: EnterpriseTokens.workspace.borderSubtle, height: '100%' }}
              styles={{ body: { padding: 16 } }}
            >
              <div style={{ marginBottom: 12 }}>{d.icon}</div>
              <Text strong style={{ display: 'block', fontSize: 15 }}>
                {d.title}
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {d.description}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};
